import { h } from 'preact'
import { useRef, useLayoutEffect, useMemo } from 'preact/hooks'
import { ActivityIndicator } from './ActivityIndicator.jsx'

/**
 * AiBubble — renders an AI assistant message.
 *
 * During streaming: shows progressively rendered markdown from buffer.
 * After finalization: renders full markdown + artifacts (Mermaid, Excalidraw, Drive).
 *
 * Props: { message, store, formatter, isStreaming, buffer }
 * message: { id, type:'ai', content, isStreaming, isFinalized, cost, timestamp }
 */
export function AiBubble({ message, store, formatter, isStreaming, buffer, status }) {
  const ref = useRef(null)
  const finalizeRef = useRef(false)

  const time = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : ''

  // During streaming: render buffer with loader sentinels for in-progress JSON blocks.
  // After streaming: the useLayoutEffect below calls formatWithArtifacts() once.
  const streamHtml = useMemo(() => {
    if (!isStreaming || !formatter) return null
    const text = buffer || ''
    const hasUnclosed = formatter.hasUnclosedJsonBlock(text)
    let textToRender = hasUnclosed
      ? text.slice(0, text.lastIndexOf('```json'))
      : text
    const { text: processed, sentinels } = formatter.replaceJsonBlocksWithLoaders(textToRender)
    let html = formatter.parseMarkdown(processed)
    html = formatter.applyLoaderSentinels(html, sentinels)
    // Append unclosed-block live loader at end
    if (hasUnclosed) {
      html +=
        '<div class="ai-artifact-loader d-flex align-items-center gap-2 text-muted small my-2 p-2">' +
        '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>' +
        '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:.15s"></span>' +
        '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:.3s"></span>' +
        '</div>'
    }
    return html
  }, [buffer, isStreaming, formatter])

  // Streaming: set innerHTML from memoized html (rAF-throttled via signal)
  // Non-streaming: run formatWithArtifacts once for full artifact rendering
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !formatter) return

    if (isStreaming) {
      if (streamHtml != null) {
        el.innerHTML = streamHtml
        // Render Mermaid only when block is closed
        if (buffer && !formatter.hasUnclosedMermaidBlock(buffer)) {
          formatter.detectAndRenderMermaid(el)
        }
      }
    } else if (!finalizeRef.current && message.content) {
      // Finalize once: render artifacts, Mermaid, Excalidraw
      finalizeRef.current = true
      formatter.formatWithArtifacts(message.content, el).then(() => {
        store?.markFinalized(message.id)
      })
    }
  }, [streamHtml, isStreaming, message.content, message.isFinalized])

  const i18n = store?.i18n

  return (
    <div class="message message-ai mb-2">
      <div class="d-flex align-items-start">
        <div class="message-avatar me-3">
          <i class="bi bi-robot fs-3 text-success"></i>
        </div>
        <div class="message-content flex-grow-1">
          <div class="message-header d-flex align-items-center mb-2">
            <strong class="me-2">{i18n?.t('ai.title') || 'AI Assistant'}</strong>
            <small class="text-muted">{time}</small>
          </div>
          <div class="message-text ai-response ai-answer" ref={ref}></div>
          {isStreaming && <ActivityIndicator i18n={i18n} status={status} />}
        </div>
      </div>
    </div>
  )
}
